import React from 'react';

/* ─── DataTable (wrapper) ───────────────────────────────────── */

export interface DataTableProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const DataTable: React.FC<DataTableProps> = ({ children, className = '', style, ...rest }) => (
  <div
    className={`overflow-x-auto rounded-lg ${className}`}
    style={{
      backgroundColor: 'var(--theme-card-background)',
      border: '1px solid var(--theme-card-border)',
      ...style,
    }}
    {...rest}
  >
    <table className="w-full">{children}</table>
  </div>
);

DataTable.displayName = 'DataTable';

/* ─── DataTableHeader (thead + tr) ──────────────────────────── */

export interface DataTableHeaderProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

export const DataTableHeader: React.FC<DataTableHeaderProps> = ({ children, className = '', style, ...rest }) => (
  <thead {...rest}>
    <tr
      className={className}
      style={{
        backgroundColor: 'var(--theme-table-header-background)',
        borderBottom: '1px solid var(--theme-card-border)',
        ...style,
      }}
    >
      {children}
    </tr>
  </thead>
);

DataTableHeader.displayName = 'DataTableHeader';

/* ─── DataTableHeadCell (th) ────────────────────────────────── */

export interface DataTableHeadCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'center' | 'right';
}

const alignClass: Record<string, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export const DataTableHeadCell: React.FC<DataTableHeadCellProps> = ({
  children,
  align = 'left',
  className = '',
  style,
  ...rest
}) => (
  <th
    className={`px-4 py-3 ${alignClass[align]} text-xs font-medium ${className}`}
    style={{ color: 'var(--theme-card-text)', ...style }}
    {...rest}
  >
    {children}
  </th>
);

DataTableHeadCell.displayName = 'DataTableHeadCell';

/* ─── DataTableBody (tbody) ─────────────────────────────────── */

export interface DataTableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

export const DataTableBody: React.FC<DataTableBodyProps> = ({ children, ...rest }) => (
  <tbody {...rest}>{children}</tbody>
);

DataTableBody.displayName = 'DataTableBody';

/* ─── DataTableRow (tr) ─────────────────────────────────────── */

export interface DataTableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  highlighted?: boolean;
  hoverable?: boolean;
}

export const DataTableRow: React.FC<DataTableRowProps> = ({
  children,
  highlighted = false,
  hoverable = true,
  className = '',
  style,
  ...rest
}) => (
  <tr
    className={`transition-colors ${className}`}
    style={{
      borderBottom: '1px solid var(--theme-card-border)',
      backgroundColor: highlighted ? 'var(--theme-button-ghost-hover)' : undefined,
      ...style,
    }}
    onMouseEnter={
      hoverable
        ? (e) => {
            if (!highlighted) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
            rest.onMouseEnter?.(e);
          }
        : rest.onMouseEnter
    }
    onMouseLeave={
      hoverable
        ? (e) => {
            if (!highlighted) e.currentTarget.style.backgroundColor = 'transparent';
            rest.onMouseLeave?.(e);
          }
        : rest.onMouseLeave
    }
    {...rest}
  >
    {children}
  </tr>
);

DataTableRow.displayName = 'DataTableRow';

/* ─── DataTableCell (td) ────────────────────────────────────── */

export interface DataTableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'center' | 'right';
}

export const DataTableCell: React.FC<DataTableCellProps> = ({
  children,
  align,
  className = '',
  style,
  ...rest
}) => (
  <td
    className={`px-4 py-3 ${align ? alignClass[align] : ''} ${className}`}
    style={style}
    {...rest}
  >
    {children}
  </td>
);

DataTableCell.displayName = 'DataTableCell';

/* ─── DataTableEmpty (empty state row) ──────────────────────── */

export interface DataTableEmptyProps {
  colSpan: number;
  children?: React.ReactNode;
  message?: string;
}

export const DataTableEmpty: React.FC<DataTableEmptyProps> = ({
  colSpan,
  children,
  message = 'Nenhum item encontrado',
}) => (
  <tr>
    <td
      colSpan={colSpan}
      className="px-4 py-8 text-center text-sm"
      style={{ color: 'var(--theme-muted-text)' }}
    >
      {children || message}
    </td>
  </tr>
);

DataTableEmpty.displayName = 'DataTableEmpty';
